import { useState } from 'react'
import './styles.scss'
import UnbeatableSection from '../../Components/UnbeatableSection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import CustomButton from '../../Components/CustomButton'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'

const serviceId  = process.env.REACT_APP_EMAILJS_SERVICE_ID
const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID
const publicId   = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const SUBJECTS = [
  'General Inquiry',
  'Request a Quote',
  'Shipment Issue',
  'Partnership Opportunity',
  'Other',
]

const CONTACT_INFO = [
  { icon: solid('phone'), label: 'Phone', value: '+31 (970) 050-31758' },
  { icon: solid('envelope'), label: 'Email', value: 'business@sapphirebridgelogistics.com' },
  { icon: solid('location-dot'), label: 'Office', value: 'Global Facility Office' },
  { icon: solid('clock'), label: 'Support Hours', value: '24 / 7 — Around the clock' },
]

const ContactUs = () => {
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})

  const { form, reset, onChange } = useForm({
    name: '',
    email: '',
    phone: '',
    subject: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => {
    onChange(e.target.name, e.target.value)
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!form.name.trim()) newErrors.name = 'Full name is required'
    if (!form.email.trim()) newErrors.email = 'Email address is required'
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Enter a valid email address'
    if (!form.subject) newErrors.subject = 'Please select a subject'
    if (!form.msg.trim()) newErrors.msg = 'Message cannot be empty'
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = validate()
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    onChange('loading', true)
    try {
      await emailjs.send(serviceId, templateId, form, publicId)
      reset()
      setSubmitted(true)
    } catch (error) {
      console.log('ERROR', error)
    }
    onChange('loading', false)
  }

  return (
    <div className="contact-us">
      <div className="contact-us__top">
        <h1>Contact Us</h1>
        <p>Our team is available around the clock to handle your logistics enquiries.</p>
      </div>

      <div className="contact-us__content">
        <section className="contact-us__content__details">
          <div className="contact-us__content__details__inner">

            <div className="contact-us__content__details__info">
              <p className="info-intro">
                Reach out for a free quote or to discuss how we can support your supply chain needs.
              </p>

              <div className="info-cards">
                {CONTACT_INFO.map(({ icon, label, value }) => (
                  <div key={label} className="info-card">
                    <div className="info-card__icon">
                      <FontAwesomeIcon icon={icon} />
                    </div>
                    <div className="info-card__body">
                      <span className="info-card__label">{label}</span>
                      <span className="info-card__value">{value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="contact-us__content__details__form">
              <h3>Send Us a Message</h3>

              {submitted ? (
                <div className="success-message">
                  <FontAwesomeIcon icon={solid('circle-check')} className="success-icon" />
                  <h4>Message Sent!</h4>
                  <p>Thank you for reaching out. We will get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>

                  {/* Row: Name + Email */}
                  <div className="cf-row">
                    <div className="cf-field">
                      <label>Full Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={handleChange}
                        className={errors.name ? 'error' : ''}
                        disabled={form.loading}
                      />
                      {errors.name && <span className="cf-error">{errors.name}</span>}
                    </div>
                    <div className="cf-field">
                      <label>Email Address</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Your email address"
                        value={form.email}
                        onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                        disabled={form.loading}
                      />
                      {errors.email && <span className="cf-error">{errors.email}</span>}
                    </div>
                  </div>

                  {/* Row: Phone + Subject */}
                  <div className="cf-row">
                    <div className="cf-field">
                      <label>Phone <span className="optional">(optional)</span></label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 234 567 8900"
                        value={form.phone}
                        onChange={handleChange}
                        disabled={form.loading}
                      />
                    </div>
                    <div className="cf-field">
                      <label>Subject</label>
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        className={errors.subject ? 'error' : ''}
                        disabled={form.loading}
                      >
                        <option value="" disabled>Select a subject</option>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {errors.subject && <span className="cf-error">{errors.subject}</span>}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="cf-field">
                    <label>Message</label>
                    <textarea
                      name="msg"
                      rows={5}
                      placeholder="How can we help you?"
                      value={form.msg}
                      onChange={handleChange}
                      className={errors.msg ? 'error' : ''}
                      disabled={form.loading}
                    />
                    {errors.msg && <span className="cf-error">{errors.msg}</span>}
                  </div>

                  <CustomButton loading={form.loading} label="Send Message" />
                </form>
              )}
            </div>

          </div>
        </section>

        <UnbeatableSection />
      </div>
    </div>
  )
}

export default ContactUs
